import { t } from '@lingui/macro'
import type {
  GeneralAttendanceRow,
  TestCountRow,
  ThresholdAlerts,
  MaternityAttendanceRow,
  MissingExamsRow,
} from '@/core/api/statistics'

export const GENERAL_AGE_BUCKETS = ['0-14', '15-24', '25-39', '40-59', '60+'] as const
export const MATERNITY_AGE_BUCKETS = ['<18', '18-24', '25-34', '35+'] as const
export const TRIMESTERS = ['T1', 'T2', 'T3'] as const
export const GENDERS = ['F', 'M', 'I'] as const

export const GENDER_LABELS: Record<string, string> = {
  F: 'Female',
  M: 'Male',
  I: 'Unknown',
}

export const TRIMESTER_LABELS: Record<string, string> = {
  T1: '1st Trimester (0-13 wk.)',
  T2: '2nd Trimester (14-27 wk.)',
  T3: '3rd Trimester (28+ wk.)',
}

export function getGenderLabel(gender: string): string {
  if (gender === 'F') return t`Female`
  if (gender === 'M') return t`Male`
  return t`Unknown`
}

export function getTrimesterLabel(trimester: string): string {
  if (trimester === 'T1') return t`1st Trimester (0-13 wk.)`
  if (trimester === 'T2') return t`2nd Trimester (14-27 wk.)`
  return t`3rd Trimester (28+ wk.)`
}

// Pivot general attendance data: rows = establishments, columns = gender x ageBucket
export type PivotedAttendanceRow = {
  establishmentName: string
  cells: Record<string, number> // key = "F|0-14", "M|15-24", etc.
  total: number
}

export function pivotGeneralAttendance(data: GeneralAttendanceRow[]): PivotedAttendanceRow[] {
  const map = new Map<string, PivotedAttendanceRow>()

  for (const row of data) {
    if (!map.has(row.establishmentName)) {
      map.set(row.establishmentName, { establishmentName: row.establishmentName, cells: {}, total: 0 })
    }
    const entry = map.get(row.establishmentName)!
    const key = `${row.gender}|${row.ageBucket}`
    entry.cells[key] = (entry.cells[key] || 0) + row.count
    entry.total += row.count
  }

  return Array.from(map.values()).sort((a, b) => a.establishmentName.localeCompare(b.establishmentName))
}

// Pivot tests performed: rows = test labels, columns = establishments
export type PivotedTestRow = {
  testLabel: string
  counts: Record<string, number> // key = establishmentName
  total: number
}

export type PivotedTestsResult = {
  mesureRows: PivotedTestRow[]
  medicalActRows: PivotedTestRow[]
  establishments: string[]
}

export function pivotTestsPerformed(data: TestCountRow[]): PivotedTestsResult {
  const establishments = new Set<string>()
  const mesureMap = new Map<string, PivotedTestRow>()
  const medicalActMap = new Map<string, PivotedTestRow>()

  for (const row of data) {
    establishments.add(row.establishmentName)
    const map = row.source === 'medical-act' ? medicalActMap : mesureMap

    if (!map.has(row.testLabel)) {
      map.set(row.testLabel, { testLabel: row.testLabel, counts: {}, total: 0 })
    }
    const entry = map.get(row.testLabel)!
    entry.counts[row.establishmentName] = (entry.counts[row.establishmentName] || 0) + row.count
    entry.total += row.count
  }

  return {
    mesureRows: Array.from(mesureMap.values()).sort((a, b) => a.testLabel.localeCompare(b.testLabel)),
    medicalActRows: Array.from(medicalActMap.values()).sort((a, b) => a.testLabel.localeCompare(b.testLabel)),
    establishments: Array.from(establishments).sort(),
  }
}

// Pivot maternity attendance: rows = establishments, columns = ageBuckets
export type PivotedMaternityRow = {
  establishmentName: string
  ageCounts: Record<string, number>
  trimesterCounts: Record<string, number>
  total: number
}

export function pivotMaternityAttendance(data: MaternityAttendanceRow[]): PivotedMaternityRow[] {
  const map = new Map<string, PivotedMaternityRow>()

  for (const row of data) {
    if (!map.has(row.establishmentName)) {
      map.set(row.establishmentName, {
        establishmentName: row.establishmentName,
        ageCounts: {},
        trimesterCounts: {},
        total: 0,
      })
    }
    const entry = map.get(row.establishmentName)!
    entry.ageCounts[row.ageBucket] = (entry.ageCounts[row.ageBucket] || 0) + row.count
    entry.trimesterCounts[row.trimester] = (entry.trimesterCounts[row.trimester] || 0) + row.count
    entry.total += row.count
  }

  return Array.from(map.values()).sort((a, b) => a.establishmentName.localeCompare(b.establishmentName))
}

// Pivot missing exams: rows = establishments, columns = trimester
export type PivotedMissingExamsRow = {
  establishmentName: string
  echoCounts: Record<string, number>
  ecgCounts: Record<string, number>
  totalEcho: number
  totalECG: number
}

export function pivotMissingExams(data: MissingExamsRow[]): PivotedMissingExamsRow[] {
  const map = new Map<string, PivotedMissingExamsRow>()

  for (const row of data) {
    if (!map.has(row.establishmentName)) {
      map.set(row.establishmentName, {
        establishmentName: row.establishmentName,
        echoCounts: {},
        ecgCounts: {},
        totalEcho: 0,
        totalECG: 0,
      })
    }
    const entry = map.get(row.establishmentName)!
    entry.echoCounts[row.trimester] = (entry.echoCounts[row.trimester] || 0) + row.missingEcho
    entry.ecgCounts[row.trimester] = (entry.ecgCounts[row.trimester] || 0) + row.missingECG
    entry.totalEcho += row.missingEcho
    entry.totalECG += row.missingECG
  }

  return Array.from(map.values()).sort((a, b) => a.establishmentName.localeCompare(b.establishmentName))
}

// Compute totals row for attendance pivot
export function computeAttendanceTotals(rows: PivotedAttendanceRow[]): Record<string, number> {
  const totals: Record<string, number> = {}
  for (const row of rows) {
    for (const [key, value] of Object.entries(row.cells)) {
      totals[key] = (totals[key] || 0) + value
    }
  }
  return totals
}

